import React from 'react';

interface Props {
  slug: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error) => void;
}

interface State {
  error: Error | null;
}

/**
 * Isolates failures from a single custom component so a bad export or thrown exception doesn't
 * blank the whole page. Logs to `console.error` in dev; the host app can provide an `onError`
 * callback to wire up its own error reporter (Datadog, etc.).
 */
export class CustomComponentErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error): void {
    this.props.onError?.(error);
    // eslint-disable-next-line no-console
    console.error(`[custom-component:${this.props.slug}] render failed:`, error);
  }

  render(): React.ReactNode {
    if (this.state.error) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      return (
        <div
          data-custom-component-error={this.props.slug}
          style={{
            padding: '0.75rem 1rem',
            color: '#6b7280',
            fontFamily: 'ui-monospace, monospace',
            fontSize: '0.75rem',
            background: '#f9fafb',
            border: '1px dashed #d1d5db',
            borderRadius: '0.375rem',
          }}
        >
          {`Custom component "${this.props.slug}" failed to render. See console.`}
        </div>
      );
    }
    return this.props.children;
  }
}
